import React, { useState, useRef } from "react";
import {
  IconButton,
  Box,
  Typography,
  Grid,
  Tooltip,
  alpha,
  useMediaQuery,
  useTheme,
  Popover,
  Divider,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import icons from "assets/icons";
// Import Lucide React icons for admin menu items
import { MailPlus, Layout, Lightbulb } from "lucide-react";
import { ThemeMode } from "config";

const QuickNavButton = ({ isAdmin = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumDevice = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const buttonRef = useRef(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigateTo = (path) => {
    navigate(path);
    handleClose();
  };

  // Create a helper for custom icon rendering
  const renderIcon = (iconSrc) => (
    <Box
      component="img"
      src={iconSrc}
      alt="icon"
      sx={{
        width: 20,
        height: 20,
        filter: "brightness(0)", // Make icons black
      }}
    />
  );

  // Create a helper for Lucide React icons
  const renderLucideIcon = (IconComponent, color = "#000000", size = 20) => (
    <IconComponent size={size} color={color} strokeWidth={2} />
  );

  // Standard navigation cards - shown to all users
  const standardNavCards = [
    {
      title: "Experts",
      iconComponent: renderIcon(icons.expert),
      path: "/experts",
    },
    {
      title: "Institutions",
      iconComponent: renderIcon(icons.institutions),
      path: "/institutions",
    },
    {
      title: "Newsletters",
      iconComponent: renderIcon(icons.news),
      path: "/newsletter",
    },
    {
      title: "Formations",
      iconComponent: renderIcon(icons.formation),
      path: "/formation",
    },
    {
      title: "Contribuer",
      iconComponent: renderLucideIcon(Lightbulb, "#16a34a"),
      path: "/contribuer",
      bgColor: "#f0fdf4",
    },
  ];

  // Admin-specific navigation cards - only shown to admins
  const adminNavCards = [
    {
      title: "Abonné",
      iconComponent: renderLucideIcon(MailPlus, "#4f46e5"),
      path: "/admin/newsletters",
      bgColor: "#f0f7ff",
    },
    {
      title: "Bannières publicitaires",
      iconComponent: renderLucideIcon(Layout, "#f97316"),
      path: "/admin/list-banners",
      bgColor: "#fff7ed",
    },
  ];

  const open = Boolean(anchorEl);
  // Theme-based styles
  const iconBackColorOpen =
    theme.palette.mode === ThemeMode.DARK
      ? "background.paper"
      : "secondary.200";
  const iconBackColor =
    theme.palette.mode === ThemeMode.DARK
      ? "background.default"
      : "secondary.100";

  return (
    <>
      <Tooltip title="Accès rapide">
        <IconButton
          ref={buttonRef}
          variant="light"
          size="large"
          onClick={handleOpen}
          color="secondary"
          sx={{
            width: 40,
            height: 40,
            color: "secondary.main",
            bgcolor: open ? iconBackColorOpen : iconBackColor,
            "&:hover": {
              bgcolor: open ? iconBackColorOpen : "secondary.200",
            },
          }}
        >
          <Box
            component="img"
            src={icons.menuSquare}
            alt="Menu"
            sx={{
              width: 24,
              height: 24,
              filter: "brightness(0)", // Make icon black
            }}
          />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right", // Position to the right of the button
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right", // Align the right edge of popover with right edge of button
        }}
        // Add these props to fix the backdrop and positioning issues
        disableScrollLock={true}
        disableRestoreFocus={true}
        disableEnforceFocus={true}
        PaperProps={{
          elevation: 4,
          sx: {
            width: isSmallDevice ? "90vw" : isMediumDevice ? "320px" : "350px",
            maxWidth: isSmallDevice ? "90vw" : "400px",
            borderRadius: 2,
            m: 0,
            p: isSmallDevice ? 1.5 : 2,
            bgcolor: "#FFFFFF",
            boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.15)",
            overflow: "visible",
            mt: 0.5, // Small space between button and popover
            position: "relative",
            zIndex: theme.zIndex.popover + 1,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          },
        }}
        style={{
          zIndex: theme.zIndex.popover + 1,
        }}
      >
        {/* Close button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: isSmallDevice ? 0.5 : 1,
          }}
        >
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{
              p: 0.5,
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Standard navigation items - visible to all users */}
        <Grid
          container
          spacing={isSmallDevice ? 1 : 2}
          sx={{
            mt: 1,
            px: isSmallDevice ? 0.5 : 1,
          }}
        >
          {standardNavCards.map((card) => (
            <Grid item xs={6} sm={6} md={6} key={card.title}>
              <Box
                onClick={() => navigateTo(card.path)}
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: isSmallDevice ? 0.8 : 1.5,
                  borderRadius: 3,
                  transition: "all 0.2s ease",
                  minHeight: isSmallDevice ? "80px" : "100px",
                  "&:hover": {
                    bgcolor: "rgba(0, 0, 0, 0.02)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: isSmallDevice ? 36 : 52,
                    height: isSmallDevice ? 36 : 52,
                    borderRadius: "50%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: isSmallDevice ? 0.8 : 1.5,
                    bgcolor: card.bgColor || "#F0F4FF",
                    "& img": {
                      width: isSmallDevice ? 16 : 20,
                      height: isSmallDevice ? 16 : 20,
                    },
                  }}
                >
                  {card.iconComponent}
                </Box>
                <Typography
                  variant="body1"
                  fontWeight={500}
                  align="center"
                  sx={{
                    fontSize: isSmallDevice ? "0.75rem" : "0.95rem",
                    lineHeight: 1.2,
                  }}
                >
                  {card.title}
                </Typography>
              </Box>
            </Grid>
          ))}

          {/* Divider and admin-specific items - only visible to admin users */}
          {isAdmin && (
            <>
              {/* Divider */}
              <Grid item xs={12}>
                <Divider sx={{ my: isSmallDevice ? 1 : 1.5, width: "100%" }} />
              </Grid>

              {/* Admin-specific navigation items */}
              {adminNavCards.map((card) => (
                <Grid item xs={6} sm={6} md={6} key={card.title}>
                  <Box
                    onClick={() => navigateTo(card.path)}
                    sx={{
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: isSmallDevice ? 0.8 : 1.5,
                      borderRadius: 3,
                      transition: "all 0.2s ease",
                      minHeight: isSmallDevice ? "80px" : "100px",
                      "&:hover": {
                        bgcolor: "rgba(0, 0, 0, 0.02)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: isSmallDevice ? 36 : 52,
                        height: isSmallDevice ? 36 : 52,
                        borderRadius: "50%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        mb: isSmallDevice ? 0.8 : 1.5,
                        bgcolor: card.bgColor || "#F0F4FF",
                      }}
                    >
                      {card.iconComponent}
                    </Box>
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      align="center"
                      sx={{
                        fontSize: isSmallDevice ? "0.75rem" : "0.95rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {card.title}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </>
          )}
        </Grid>
      </Popover>
    </>
  );
};

export default QuickNavButton;
